import type { ExperimentConfig } from '@/types';

const experiment1Config: ExperimentConfig = {
  id: 'exp1',
  number: 1,
  name: 'Emission Spectra',
  description: 'Determine the wavelengths of visible light associated with hydrogen using a prism spectrometer, calibrated with helium, and identify the electron transitions associated with them. Also observe the spectrum of neon or argon.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Understand that electrons in atoms occupy discrete energy levels',
    'Learn to calibrate a prism spectrometer using helium known wavelengths and determine a relationship between wavelength and angle',
    'Observe the visible emission spectrum of hydrogen (Red, Blue, Purple, Purple (weak) — 4 lines)',
    'Apply the calibration to determine hydrogen wavelengths from recorded angles',
    'Calculate the Rydberg constant (Rₕ = 1.09678 × 10⁷ m⁻¹) from experimental data',
    'Apply the Rydberg formula: 1/λ = Rₕ × (1/n₁² - 1/n₂²) where n₁ = 2 for visible transitions',
    'Compare line spectra of different elements (H, He, Ne/Ar)',
    'Calculate percent error between experimental and accepted wavelengths and Rydberg constant',
  ],
  safetyNotes: [
    'High voltage equipment - do not touch discharge tubes while powered',
    'Do not look directly at discharge tubes for extended periods - UV radiation may be emitted',
    'LAMP MAY BECOME HOT DURING USE - allow it to cool for a few minutes before switching it out',
    'ONLY handle the bulbs by the larger ends, not the slim centre piece - they are somewhat fragile',
    'Lift spectrometer using the base only, not the arms - keep it level in transport',
  ],
  requiredEquipment: [
    'Prism spectrometer with Vernier scale',
    'Hydrogen discharge tube',
    'Helium discharge tube (for calibration)',
    'Neon or Argon discharge tube (for observation)',
    'High voltage power supply (lamp)',
    'Wood clamp (to secure spectrometer to bench)',
    'Cloth (to cover prism and tube ends)',
    'Excel template (from DC Connect) for wavelength prediction',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'setup',
      title: 'Part I: Setup and Safety',
      equipment: ['Prism spectrometer', 'Power supply (lamp)', 'Discharge tubes', 'Wood clamp', 'Cloth'],
      initialState: {
        safetyAcknowledged: false,
        calibrated: false,
        selectedGas: null,
        isPowered: false,
        spectrometerAngle: 90,
      },
      steps: [
        {
          id: 1,
          title: 'Safety Check',
          shortDescription: 'Acknowledge high voltage safety warnings',
          handler: 'handleSafetyAcknowledgement',
          infoContent: {
            title: 'Safety Warning',
            content: 'This experiment uses high voltage power supplies to excite gas discharge tubes. Never touch the tubes or connections while powered. LAMP MAY BECOME HOT DURING USE — allow it to cool for a few minutes before switching it out. ONLY handle the bulbs by the larger ends, not the slim centre piece — they are somewhat fragile. Lift the spectrometer using the base only, not the arms, and keep it level in transport.',
            whatHappens: 'You review and acknowledge safety precautions for high-voltage discharge equipment before handling any apparatus.',
            observe: [
              'Identify the high-voltage power supply and its connections',
              'Note that discharge tubes become hot during operation',
              'Handle bulbs only by the larger ends, never the slim centre piece',
            ],
          },
        },
        {
          id: 2,
          title: 'Calibrate Spectrometer',
          shortDescription: 'Set spectrometer on level surface and calibrate with known source',
          handler: 'handleCalibrateSpectrometer',
          validation: {
            requires: ['safetyAcknowledged'],
          },
          infoContent: {
            title: 'Spectrometer Calibration',
            content: `Set up the prism spectrometer per the procedure:

1. Place spectrometer on bench close to the edge. Use a **wood clamp** to secure it — **just snug enough to prevent movement, do not clamp too hard!**
2. Ensure the **slit on the collimator is vertical**. Open the slit fully by turning the slit adjustment knob clockwise. Arrange so the collimator arm is **perpendicular to the bench edge**.
3. Place the **helium lamp** in front of the collimator, very close but not touching.
4. Place the **cloth** over the prism and ends of the tubes.
5. Look through the eyepiece and search for crosshairs and coloured lines. Adjust lamp position iteratively.
6. Use the **Vernier scale** to record angles for all helium lines (Purple, Blue (weak), Green (weak), Green, Yellow, Red, Red (weak)).
7. The helium data establishes the **angle-to-wavelength calibration curve** (y = mx + b from Excel).`,
            whatHappens: 'The prism spectrometer is secured, aligned, and calibrated using helium emission lines of known wavelengths to establish an angle-to-wavelength relationship.',
            observe: [
              'Ensure the collimator slit is vertical and fully open initially',
              'Through the eyepiece, locate crosshairs and coloured helium lines (7 lines total)',
              'Record the Vernier scale angle for each helium line precisely',
              'Verify the calibration produces a linear relationship in Excel (y = mx + b)',
            ],
          },
        },
        {
          id: 3,
          title: 'Select Gas Tube',
          shortDescription: 'Choose hydrogen tube from the rack',
          handler: 'handleSelectGasTube',
          validation: {
            requires: ['calibrated'],
          },
          infoContent: {
            title: 'Gas Discharge Tubes',
            content: 'Begin with either hydrogen or helium light sources, as these provide the data for the calculations. ONLY handle the bulbs by the larger ends, not the slim centre piece — they are somewhat fragile. To load: place the bottom end into the base, push down gently until the top fits in, then release allowing it to push upwards. Install the power cord, plug in, and test.',
            whatHappens: 'A gas discharge tube is selected and carefully loaded into the high-voltage power supply base for spectral observation.',
            observe: [
              'Handle the tube only by the larger ends to avoid breakage',
              'Seat the tube securely in the base by pressing down gently and releasing',
              'Confirm the power cord is connected before testing',
            ],
          },
        },
        {
          id: 4,
          title: 'Power On',
          shortDescription: 'Turn on the discharge tube power supply',
          handler: 'handlePowerOn',
          validation: {
            requires: ['selectedGas'],
          },
          infoContent: {
            title: 'Atomic Excitation',
            content: `The high voltage excites electrons in the gas atoms to higher energy levels. When they fall back down, they emit photons of specific wavelengths characteristic of that element.

**Observe:** Place the lamp very close to but not touching the collimator end with the slit fully open. Place the cloth over the prism and ends of the tubes. Look through the eyepiece and search for crosshairs and coloured lines. If you see nothing, slowly move the lamp sideways at the slit — move the lamp, then look, repeat until lines are visible.

Once visible, lock the eyepiece in place using the finger screw. Rotate and adjust the tube ends to focus until the spectra are clear. Open/close the slit iteratively until lines are very narrow and clear.`,
            whatHappens: 'High voltage excites gas atoms, promoting electrons to higher energy levels; as they relax, they emit photons at wavelengths characteristic of the element.',
            observe: [
              'The discharge tube glows with a colour characteristic of the gas (e.g., pink-violet for hydrogen)',
              'Through the eyepiece, coloured spectral lines appear against a dark background',
              'Adjust the lamp position and slit width until lines are narrow and sharply defined',
              'Lock the eyepiece with the finger screw once lines are visible',
            ],
          },
        },
      ]
    },
    {
      id: 2,
      name: 'measurement',
      title: 'Part II: Measuring Hydrogen Spectrum',
      equipment: ['Prism spectrometer', 'Vernier scale'],
      initialState: {
        recordedLines: [],
        currentLineIndex: 0,
        spectrometerAngle: 90
      },
      steps: [
        {
          id: 1,
          title: 'Find Red Line',
          shortDescription: 'Rotate spectrometer to locate the red hydrogen line',
          handler: 'handleFindLine',
          infoContent: {
            title: 'Hydrogen Red Line',
            content: `Locate the bright **red** hydrogen line. Use the fine adjustment knob to place the crosshairs onto the spectral line.

**Procedure:** Use the Vernier scale to read the angle of the red line. Record your data. The Vernier scale has an arrow and 10 additional lines — each represents a tenth of a degree.

For all visible transitions of hydrogen, n₁ = 2. We can calculate which energy level an electron fell from based on the colour we observe.`,
            chemicalEquation: '1/λ = R(1/n₁² - 1/n₂²), n₁ = 2',
            whatHappens: 'The bright red hydrogen emission line (~656 nm) corresponds to the n=3 to n=2 electron transition in the Balmer series.',
            observe: [
              'A distinct bright red line is visible through the eyepiece',
              'Align the crosshairs precisely on the centre of the red line',
              'Read the Vernier scale angle carefully, noting tenths of a degree',
            ],
          }
        },
        {
          id: 2,
          title: 'Record Red Line Angle',
          shortDescription: 'Align crosshair and record Vernier scale angle',
          handler: 'handleRecordMeasurement',
          validation: {
            condition: 'angleInRange_redLine'
          }
        },
        {
          id: 3,
          title: 'Find Blue Line',
          shortDescription: 'Locate the blue hydrogen line',
          handler: 'handleFindLine',
          infoContent: {
            title: 'Hydrogen Blue Line',
            content: 'Move the crosshairs to locate the **blue** hydrogen line. Record the angle using the Vernier scale.',
            whatHappens: 'The blue hydrogen emission line (~486 nm) corresponds to the n=4 to n=2 electron transition in the Balmer series.',
            observe: [
              'A blue line appears at a different angle than the red line',
              'This line is typically brighter than the purple lines but dimmer than red',
              'Align the crosshairs precisely and record the Vernier scale angle',
            ],
          }
        },
        {
          id: 4,
          title: 'Record Blue Line Angle',
          shortDescription: 'Align crosshair and record Vernier scale angle',
          handler: 'handleRecordMeasurement',
          validation: {
            condition: 'angleInRange_blueLine'
          }
        },
        {
          id: 5,
          title: 'Find Purple Line',
          shortDescription: 'Locate the purple hydrogen line',
          handler: 'handleFindLine',
          infoContent: {
            title: 'Hydrogen Purple Line',
            content: 'Move the crosshairs to locate the **purple** hydrogen line. Record the angle using the Vernier scale.',
            whatHappens: 'The purple hydrogen emission line (~434 nm) corresponds to the n=5 to n=2 electron transition in the Balmer series.',
            observe: [
              'A purple/violet line is visible at a higher angle than the blue line',
              'This line is noticeably dimmer than the red and blue lines',
              'Record the angle carefully using the Vernier scale',
            ],
          }
        },
        {
          id: 6,
          title: 'Record Purple Line Angle',
          shortDescription: 'Align crosshair and record Vernier scale angle',
          handler: 'handleRecordMeasurement',
          validation: {
            condition: 'angleInRange_purpleLine'
          }
        },
        {
          id: 7,
          title: 'Find Purple (weak) Line',
          shortDescription: 'Locate the weak purple hydrogen line',
          handler: 'handleFindLine',
          infoContent: {
            title: 'Hydrogen Purple (weak) Line',
            content: 'Locate the faint **purple (weak)** hydrogen line. This line may be difficult to see. Record the angle using the Vernier scale.',
            whatHappens: 'The faint purple hydrogen emission line (~410 nm) corresponds to the n=6 to n=2 electron transition, which is weak because fewer electrons undergo this higher-energy transition.',
            observe: [
              'This line is very faint and may require careful searching',
              'It appears at a slightly higher angle than the brighter purple line',
              'Adjust the slit width if needed to improve visibility of this weak line',
            ],
          }
        },
        {
          id: 8,
          title: 'Record Purple (weak) Line Angle',
          shortDescription: 'Align crosshair and record Vernier scale angle',
          handler: 'handleRecordMeasurement',
          validation: {
            condition: 'angleInRange_purpleWeakLine'
          }
        }
      ]
    },
    {
      id: 3,
      name: 'calculation',
      title: 'Part III: Calculate Rydberg Constant',
      equipment: [],
      initialState: {
        calculatedRh: null,
        percentError: null,
        calculationComplete: false
      },
      steps: [
        {
          id: 1,
          title: 'Review Data',
          shortDescription: 'Verify all wavelength measurements in data table',
          handler: 'handleReviewData',
          validation: {
            requires: ['recordedLines']
          }
        },
        {
          id: 2,
          title: 'Calculate Rydberg Constant',
          shortDescription: 'Apply Rydberg formula to calculate R_H',
          handler: 'handleCalculateRydberg',
          infoContent: {
            title: 'Rydberg Formula',
            content: `The Rydberg formula relates wavelength to energy level transitions:

**1/λ = R × (1/n₁² - 1/n₂²)**

Where R = the Rydberg constant (**1.09678 × 10⁷ m⁻¹**), n₁ = the initial quantum energy state and n₂ = the quantum energy state after the transition with n₂ > n₁.

For the Balmer series (visible lines), n₁ = 2. Use the wavelengths calculated from your helium calibration and the Excel template to determine Rₕ for each hydrogen line, then compare to the accepted value.

The template uses the equation of the line (y = mx + b) from the helium calibration graph. Using the hydrogen angles as your "y" values, the template calculates the wavelengths.`,
            chemicalEquation: '1/λ = R × (1/n₁² - 1/n₂²)',
            whatHappens: 'The measured hydrogen angles are converted to wavelengths via the helium calibration curve, then the Rydberg constant is calculated from each line using the Rydberg formula.',
            observe: [
              'Use the Excel template to convert hydrogen angles to wavelengths via the calibration equation',
              'Calculate R_H for each hydrogen line (Red n=3, Blue n=4, Purple n=5, Purple weak n=6)',
              'Compare each calculated R_H to the accepted value of 1.09678 x 10^7 m^-1',
            ],
          }
        },
        {
          id: 3,
          title: 'Calculate Percent Error',
          shortDescription: 'Compare calculated R_H to accepted value',
          handler: 'handleCalculateError',
          infoContent: {
            title: 'Accepted Value',
            content: 'The accepted value of the Rydberg constant is **R = 1.09678 × 10⁷ m⁻¹**. Calculate your Rydberg constant and the % difference to the expected value. Also calculate % error between your calculated wavelengths for hydrogen and the actual values (look these up).',
            whatHappens: 'The experimentally determined Rydberg constant and hydrogen wavelengths are compared with accepted literature values to assess measurement accuracy.',
            observe: [
              'Calculate percent error: |experimental - accepted| / accepted x 100%',
              'Compare experimental wavelengths against accepted values (656.3, 486.1, 434.0, 410.2 nm)',
              'Typical student error should be within a few percent of accepted values',
            ],
          }
        }
      ]
    },
    {
      id: 4,
      name: 'comparison',
      title: 'Part IV: Compare Spectra',
      equipment: ['Helium discharge tube', 'Neon or Argon discharge tube'],
      initialState: {
        comparisonGas: null,
        observationsRecorded: false
      },
      steps: [
        {
          id: 1,
          title: 'Switch to Helium',
          shortDescription: 'Replace hydrogen tube with helium tube',
          handler: 'handleSwitchGas',
          infoContent: {
            title: 'Helium Spectrum',
            content: 'Helium has two electrons and produces a more complex spectrum than hydrogen. Record the angle for all helium lines: **Purple, Blue (weak), Green (weak), Green, Yellow, Red, Red (weak)** — 7 lines total. The helium wavelengths are provided to you. Use these to determine a relationship between wavelength and recorded angle (calibration curve in Excel).',
            whatHappens: 'The helium discharge tube replaces hydrogen; helium produces seven visible lines used to build the calibration curve relating angle to wavelength.',
            observe: [
              'Helium produces more spectral lines than hydrogen (7 vs 4 visible lines)',
              'Note the range of colours: Purple, Blue (weak), Green (weak), Green, Yellow, Red, Red (weak)',
              'Record Vernier angles for all seven helium lines for calibration',
            ],
          }
        },
        {
          id: 2,
          title: 'Observe Helium Spectrum',
          shortDescription: 'Note the different line pattern compared to hydrogen',
          handler: 'handleObserveSpectrum'
        },
        {
          id: 3,
          title: 'Switch to Neon or Argon',
          shortDescription: 'Replace helium tube with neon or argon tube',
          handler: 'handleSwitchGas',
          infoContent: {
            title: 'Neon/Argon Spectrum',
            content: 'For Neon or Argon, you do not need to record all the readings — simply observe and get a count of all the lines, how many of each colour, how close together, etc.',
            whatHappens: 'A neon or argon discharge tube is installed for qualitative observation of its much more complex emission spectrum compared to hydrogen and helium.',
            observe: [
              'Neon produces many closely spaced lines, predominantly in the red-orange region',
              'Count the total number of visible lines and note the dominant colours',
              'Compare the line density and spacing to the simpler hydrogen and helium spectra',
            ],
          }
        },
        {
          id: 4,
          title: 'Observe Neon/Argon Spectrum',
          shortDescription: 'Count lines, note colours and spacing',
          handler: 'handleObserveSpectrum'
        },
        {
          id: 5,
          title: 'Record Observations',
          shortDescription: 'Document differences between spectra',
          handler: 'handleRecordObservations',
          infoContent: {
            title: 'Why Different Spectra?',
            content: `Each element has unique energy levels. Record your observations for the formal lab report.

**Lab Questions:**
1. How many different spectral lines were observed for Hydrogen? Helium? Neon/Argon? How many were expected — if there was a difference, explain why.
2. What causes the spectral lines to appear?
3. What determines the number of spectral lines?
4. What is the difference between your Rydberg constant and that of the literature? Provide at least one reason they differ.`,
            whatHappens: 'Observations from all three gas spectra are compiled and compared, demonstrating that each element has a unique set of energy levels producing a characteristic line spectrum.',
            observe: [
              'Hydrogen: 4 visible lines (Red, Blue, Purple, Purple weak)',
              'Helium: 7 visible lines spanning a wider colour range',
              'Neon/Argon: many more lines, often closely spaced, reflecting more complex electron configurations',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp1',
    title: 'Emission Spectra Quiz',
    questions: [
      {
        id: 'exp1-q1',
        type: 'mcq' as const,
        question: 'How many visible emission lines does hydrogen produce?',
        options: [
          { id: 'a', text: '2 lines' },
          { id: 'b', text: '3 lines' },
          { id: 'c', text: '4 lines' },
          { id: 'd', text: '6 lines' },
        ],
        correctOptionId: 'c',
        explanation: 'Hydrogen has 4 visible lines in the Balmer series: red (656 nm), blue (486 nm), and two purple lines (434 nm and 410 nm).',
      },
      {
        id: 'exp1-q2',
        type: 'mcq' as const,
        question: 'In the Rydberg formula 1/λ = Rₕ(1/n₁\u00B2 - 1/n₂\u00B2), what is the value of n₁ for the visible (Balmer) series?',
        options: [
          { id: 'a', text: 'n₁ = 1' },
          { id: 'b', text: 'n₁ = 2' },
          { id: 'c', text: 'n₁ = 3' },
          { id: 'd', text: 'n₁ = 4' },
        ],
        correctOptionId: 'b',
        explanation: 'For the Balmer (visible) series, n₁ = 2. The Lyman series uses n₁ = 1 (UV) and Paschen uses n₁ = 3 (IR).',
      },
      {
        id: 'exp1-q3',
        type: 'calculation' as const,
        question: 'The accepted value of the Rydberg constant is 1.09678 × 10⁷ m⁻\u00B9. Express this value in m⁻\u00B9 (enter the number without the × 10⁷ part):',
        expectedValue: 1.09678,
        tolerance: 1,
        unit: '× 10⁷ m⁻\u00B9',
      },
      {
        id: 'exp1-q4',
        type: 'short_answer' as const,
        question: 'Why is helium used to calibrate the spectrometer instead of hydrogen?',
        sampleAnswer: 'Helium has well-characterized, precisely known emission wavelengths that are spread across the visible spectrum. Using these known wavelengths, a calibration curve relating angle to wavelength can be constructed, which is then used to determine the unknown hydrogen wavelengths.',
      },
      {
        id: 'exp1-q5',
        type: 'mcq' as const,
        question: 'What does a line emission spectrum indicate about the energy levels in an atom?',
        options: [
          { id: 'a', text: 'Electrons can have any energy value' },
          { id: 'b', text: 'Electrons occupy discrete (quantized) energy levels' },
          { id: 'c', text: 'All atoms emit the same wavelengths' },
          { id: 'd', text: 'Energy is continuously emitted as electrons orbit' },
        ],
        correctOptionId: 'b',
        explanation: 'Discrete emission lines prove that electrons in atoms occupy specific quantized energy levels. Photons are emitted only when electrons transition between these fixed levels.',
      },
    ],
  },
};

export default experiment1Config;
